import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { _ as _to_property_key } from "@swc/helpers/_/_to_property_key";
import { jsx as _jsx } from "react/jsx-runtime";
import React, { Fragment, useMemo } from 'react';
import { Box, Row } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { useLocaleKeys } from '~/i18n';
import { useAgreementsAiSearchStore } from '../hooks/useAgreementsAiSearchStore';
import { SetPredicateChip } from './SetPredicateChip/SetPredicateChip';
export const GetAgreementPredicateChipsTID = (parentTestId)=>getTestIds(parentTestId);
export const AgreementPredicateChips = (param)=>{
    let { testId } = param;
    const { AgreementPredicateChipsTestIds } = useMemo(()=>GetAgreementPredicateChipsTID(testId), [
        testId
    ]);
    const { t } = useLocaleKeys('agreement');
    const { t: commonFilter } = useLocaleKeys('common.filter');
    const filter = useAgreementsAiSearchStore((s)=>s.filter);
    const editFilter = useAgreementsAiSearchStore((s)=>s.editFilter);
    if (filter == null) return null;
    const setField = (key, value)=>{
        if (value == null) {
            const { [key]: _omit } = filter, rest = _object_without_properties(filter, [
                _to_property_key(key)
            ]);
            editFilter(rest);
        } else {
            editFilter(_object_spread_props(_object_spread({}, filter), {
                [key]: value
            }));
        }
    };
    const statusOptions = [
        {
            value: 'active',
            label: t.agreementStatusOptions.active()
        },
        {
            value: 'exhausted',
            label: t.agreementStatusOptions.completed()
        },
        {
            value: 'terminated',
            label: t.agreementStatusOptions.terminated()
        }
    ];
    const paymentMethodOptions = [
        {
            value: 'credit',
            label: t.paymentMethodOptions.card()
        },
        {
            value: 'bank',
            label: t.paymentMethodOptions.bankAccount()
        },
        {
            value: 'verificationInitiated',
            label: t.paymentMethodOptions.manualVerificationInitiated()
        },
        {
            value: 'verificationFailed',
            label: t.paymentMethodOptions.manualVerificationFailed()
        },
        {
            value: 'notSet',
            label: t.paymentMethodOptions.notSet()
        }
    ];
    const hasCreditOptions = [
        {
            value: 'yes',
            label: t.hasCreditOptions.hasCredit()
        },
        {
            value: 'no',
            label: t.hasCreditOptions.none()
        }
    ];
    const priceIncreaseOptions = [
        {
            value: 'automatic',
            label: t.priceIncreaseOptions.included()
        },
        {
            value: 'none',
            label: t.priceIncreaseOptions.notIncluded()
        }
    ];
    const isText = ()=>t.aiChipOperator.is();
    const isNotText = ()=>t.aiChipOperator.isNot();
    const applyText = commonFilter.apply();
    const selectAllText = commonFilter.selectAll();
    const units = [];
    if (filter.status != null) {
        units.push({
            key: 'status',
            node: /*#__PURE__*/ _jsx(SetPredicateChip, {
                testId: AgreementPredicateChipsTestIds.Status(),
                fieldLabel: t.filterLabels.agreementStatus(),
                options: statusOptions,
                predicate: filter.status,
                onChange: (value)=>setField('status', value),
                isText: isText,
                isNotText: isNotText,
                applyText: applyText,
                selectAllText: selectAllText,
                biPrefix: "agreements_ai_chip_status"
            })
        });
    }
    if (filter.paymentMethod != null) {
        units.push({
            key: 'paymentMethod',
            node: /*#__PURE__*/ _jsx(SetPredicateChip, {
                testId: AgreementPredicateChipsTestIds.PaymentMethod(),
                fieldLabel: t.filterLabels.paymentMethod(),
                options: paymentMethodOptions,
                predicate: filter.paymentMethod,
                onChange: (value)=>setField('paymentMethod', value),
                isText: isText,
                isNotText: isNotText,
                applyText: applyText,
                selectAllText: selectAllText,
                biPrefix: "agreements_ai_chip_payment_method"
            })
        });
    }
    if (filter.hasCredit != null) {
        units.push({
            key: 'hasCredit',
            node: /*#__PURE__*/ _jsx(SetPredicateChip, {
                testId: AgreementPredicateChipsTestIds.HasCredit(),
                fieldLabel: t.filterLabels.hasCredit(),
                options: hasCreditOptions,
                predicate: filter.hasCredit,
                onChange: (value)=>setField('hasCredit', value),
                isText: isText,
                isNotText: isNotText,
                applyText: applyText,
                selectAllText: selectAllText,
                biPrefix: "agreements_ai_chip_has_credit"
            })
        });
    }
    if (filter.automaticPriceIncrease != null) {
        units.push({
            key: 'automaticPriceIncrease',
            node: /*#__PURE__*/ _jsx(SetPredicateChip, {
                testId: AgreementPredicateChipsTestIds.PriceIncrease(),
                fieldLabel: t.filterLabels.servicePriceIncreaseState(),
                options: priceIncreaseOptions,
                predicate: filter.automaticPriceIncrease,
                onChange: (value)=>setField('automaticPriceIncrease', value),
                isText: isText,
                isNotText: isNotText,
                applyText: applyText,
                selectAllText: selectAllText,
                biPrefix: "agreements_ai_chip_price_increase"
            })
        });
    }
    if (units.length === 0) return null;
    return /*#__PURE__*/ _jsx(Box, {
        testId: AgreementPredicateChipsTestIds.Root(),
        children: /*#__PURE__*/ _jsx(Row, {
            alignItems: "center",
            gap: 8,
            wrap: true,
            children: units.map((unit)=>/*#__PURE__*/ _jsx(Fragment, {
                    children: unit.node
                }, unit.key))
        })
    });
};
